import { Directive, AfterViewInit, OnDestroy, Input, ElementRef, NgZone, OnInit, Inject, Output, EventEmitter } from '@angular/core';
import { Subject, fromEvent } from 'rxjs';
import { switchMap, map, takeUntil, take } from 'rxjs/operators';
import { GalleryTypeService } from './services/gallery-type.service';

/** Apply this to an HTML element to configure resizing */
@Directive({
  selector: '[appResize]'
})
export class ResizeDirective implements OnInit, AfterViewInit, OnDestroy {
  /** Optional. The CSS identifier of the Html element the user will drag to resize the target. Usually a bordering element of the target.
   * If omitted, uses the element this directive is attached to. */
  @Input() dragHandle: string;
  /** The CSS identifier of the Html element the intended to be resized. */
  @Input() dragTarget: string;
  /** The range of widths the target can be resized to. First number is minimum; second number is maximum.
   *
   * @default [10, 1080] */
  @Input() widthRange: [number, number] = [10, 1080];
  /** Emits the new selected width value (number) when width adjustment is complete. */
  @Output() widthAdjustedEmitter = new EventEmitter<number>();
  private target: HTMLElement;
  private handle: HTMLElement;
  private destroy$ = new Subject<void>();
  private delta: number; // x change
  private transitionStorage = '';

  constructor(@Inject(ElementRef) private elementRef: ElementRef, private zone: NgZone, private gallerySvc: GalleryTypeService) { }

  ngOnInit() {
    if (!this.dragTarget) {
      throw new Error('Resizable target not specified.');
    }
  }

  ngAfterViewInit() {
    try {
      this.handle = this.dragHandle ?
        document.querySelector(this.dragHandle) as HTMLElement
        : this.elementRef.nativeElement;
      this.target = document.querySelector(this.dragTarget) as HTMLElement;
      this.gallerySvc.libraryFilterBarWidth$.pipe(take(1)).subscribe(n => this.target.style.width = n + 'px');
      this.transitionStorage = this.target.style.transition;
      this.handle.style.userSelect = 'none'; // prevent highlighting of text in handle
    } catch {
      throw new Error('Could not bind to resizable target or handle.');
    }
    setTimeout(() => this.setupEvents(), 100);
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private setupEvents(): void {
    this.zone.runOutsideAngular(() => {
      const mousedown$ = fromEvent(this.handle, 'mousedown');
      const mousemove$ = fromEvent(document, 'mousemove');
      const mouseup$ = fromEvent(document, 'mouseup');
      const mousedrag$ = mousedown$.pipe(switchMap(
        (e: MouseEvent) => {
          this.target.style.transition = 'unset';
          const startX = e.clientX - this.handle.offsetLeft;
          return mousemove$.pipe(
            map((event: MouseEvent) => {
              event.preventDefault();
              // const targetRect = this.target.getBoundingClientRect();
              this.delta = Math.abs(startX - event.clientX);
              if (this.delta === 0) {
                return;
              }
              if (this.delta < this.widthRange[0]) {
                this.delta = this.widthRange[0];
              }
              if (this.delta > this.widthRange[1]) {
                this.delta = this.widthRange[1];
              }
              // requestAnimationFrame(() => {
                this.target.style.width = this.delta + 'px';
              // });
            }),
            takeUntil(mouseup$)
          );
        }
      ),
        takeUntil(this.destroy$));
      mousedrag$.subscribe();
      mouseup$.pipe(takeUntil(this.destroy$))
        .subscribe(() => {
          this.delta = 0;
          this.target.style.transition = this.transitionStorage; // restore CSS settings
          this.widthAdjustedEmitter.emit(this.target.offsetWidth);
          this.gallerySvc.libraryFilterBarWidth = this.target.offsetWidth;
      });
    });
  }

}
